import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import { lighten } from 'color2k';
import { UspItem } from './usp-item';
import { BOISvg, SvgIcon } from '$components/elements/svg-icon';
import { Text } from '$components/elements/text';
import { Flex } from '$components/layouts';
import { useTranslation } from '$hooks';
import { formatString } from '$lib/helpers';
import { IExpresseDeliveryResponse } from '$models';
import { Button } from '$components/elements/button';
import { B2CDeliveryMethod, checkoutPageUrl } from '$constants';
import { getCityFromZipcode } from '$services/zipcode.service';
import { useTempBasket } from '~/store/temp-basket';
import { theme } from '$theme';

type Props = {
    selectedZipCode?: string;
    itemNumber?: string;
    sameDayDeliveryData?: IExpresseDeliveryResponse | null;
    deliveryMethod?: number;
    onClick: () => void;
};

export const DeliveryUsp = ({ selectedZipCode, sameDayDeliveryData, itemNumber, deliveryMethod, onClick }: Props) => {
    const { translate } = useTranslation();
    const { push } = useRouter();
    const { createExpressBasket } = useTempBasket();
    const [isCreatingTempBasket, setIsCreatingTempBasket] = useState(false);

    const onExpressDelivery = async () => {
        if (!itemNumber && !sameDayDeliveryData?.storeInformation?.customerId) {
            return;
        }
        setIsCreatingTempBasket(true);
        const success = await createExpressBasket(itemNumber, selectedZipCode, sameDayDeliveryData?.storeInformation);
        setIsCreatingTempBasket(false);

        if (success) {
            push(checkoutPageUrl);
        }
    };

    const expressDeliveryOption = useMemo(
        () => sameDayDeliveryData?.storeDeliveryShippingTypes?.[0],
        [sameDayDeliveryData]
    );

    const isPreorder = useMemo(
        () =>
            deliveryMethod === B2CDeliveryMethod.Preorder ||
            deliveryMethod === B2CDeliveryMethod.PreorderDirectOnly ||
            deliveryMethod === B2CDeliveryMethod.PreorderShopAndDirect ||
            deliveryMethod === B2CDeliveryMethod.PreorderShopOnly,
        [deliveryMethod]
    );

    const deliveryContent: {
        title: string;
        description: string;
        icon: keyof typeof BOISvg;
    } = useMemo(() => {
        // Delivery makes same day unavailable
        if (deliveryMethod === B2CDeliveryMethod.DirectOnly) {
            return {
                title: selectedZipCode
                    ? formatString(
                          translate('productPage.uspDelivery.regularDeliveryZipCodeTitle'),
                          getCityFromZipcode(selectedZipCode) ?? selectedZipCode
                      )
                    : translate('productPage.uspDelivery.regularDeliveryTitle'),
                description: selectedZipCode
                    ? translate('productPage.uspDelivery.regularDeliveryZipCodeDescription')
                    : translate('productPage.uspDelivery.regularDeliveryDescription'),
                icon: 'bikefast',
            };
        }

        // Delivery is download
        if (deliveryMethod === B2CDeliveryMethod.Download) {
            return {
                title: translate('productPage.uspDelivery.downloadDeliveryTitle'),
                description: translate('productPage.uspDelivery.downloadDeliveryDescription'),
                icon: 'download',
            };
        }

        // Delivery is shop only
        if (deliveryMethod === B2CDeliveryMethod.ShopOnly) {
            return {
                title: translate('productPage.uspDelivery.shopOnlyTitle'),
                description: translate('productPage.uspDelivery.shopOnlyDescription'),
                icon: 'house',
            };
        }

        // Delivery is print on demand
        if (deliveryMethod === B2CDeliveryMethod.PrintOnDemand) {
            return {
                title: translate('productPage.uspDelivery.printOnDemandTitle'),
                description: translate('productPage.uspDelivery.printOnDemandDescription'),
                icon: 'truck',
            };
        }

        if (selectedZipCode && !sameDayDeliveryData?.storeInformation) {
            return {
                title: formatString(
                    translate('productPage.uspDelivery.notAvailableTitle'),
                    selectedZipCode +
                        (getCityFromZipcode(selectedZipCode) ? ` ${getCityFromZipcode(selectedZipCode)}` : '')
                ),
                description: translate('productPage.uspDelivery.notAvailableDescription'),
                icon: 'truck',
            };
        }

        // Zipcode is known and same day delivery is available
        if (selectedZipCode && sameDayDeliveryData?.storeInformation && expressDeliveryOption) {
            return {
                title: formatString(
                    translate('productPage.uspDelivery.sameDayAllowedTitle'),
                    selectedZipCode +
                        (getCityFromZipcode(selectedZipCode) ? ` ${getCityFromZipcode(selectedZipCode)}` : '')
                ),
                description: formatString(
                    translate('productPage.uspDelivery.sameDayAllowedDescription'),
                    sameDayDeliveryData?.storeInformation?.storeDeliveryCutoffTime || '',
                    expressDeliveryOption.deliveryTime?.toLowerCase() || ''
                ),
                icon: 'bikefast',
            };
        }
        // Zipcode is known but same day delivery is not available

        return {
            title: translate('productPage.uspDelivery.defaultTitle'),
            description: translate('productPage.uspDelivery.defaultDescription'),
            icon: 'bikefast',
        };
    }, [selectedZipCode, expressDeliveryOption, sameDayDeliveryData, deliveryMethod]);

    // TODO clarify if this is the correct behavior
    if (isPreorder) {
        return <></>;
    }

    return (
        <>
            <UspItem onClick={onClick} hideButtonBorder>
                <IconContainer>
                    <SvgIcon svg={deliveryContent.icon} size={28} color={theme.colors.secondaryBlueDarkBase} />
                </IconContainer>
                <TextContainer column flex gap="extrasmall">
                    <Text fontWeight="Bold" color={theme.colors.secondaryBlueDarkBase}>
                        {deliveryContent.title}
                    </Text>
                    <Text variant="caption" fontWeight="Medium" color={theme.colors.secondaryBlueDarkBase}>
                        {deliveryContent.description}
                    </Text>
                </TextContainer>
                <SvgIcon svg="chevronRight" size={24} />
            </UspItem>
            {selectedZipCode && sameDayDeliveryData?.storeInformation && expressDeliveryOption && (
                <AdditionalCtaContainer>
                    <AdditionalCtaButton width="expansive" onClick={onExpressDelivery} loading={isCreatingTempBasket}>
                        {translate('productPage.uspDelivery.buyNowButtonText')}
                    </AdditionalCtaButton>
                </AdditionalCtaContainer>
            )}
        </>
    );
};

const IconContainer = styled(Flex)({
    minWidth: '40px',
    height: '100%',
    minHeight: '40px',
    alignItems: 'flex-start',
    justifyContent: 'center',
});

const AdditionalCtaContainer = styled(Flex)(({ theme }) => ({
    marginTop: theme.space[2],
    padding: `0 ${theme.space[5]} ${theme.space[5]}`,
}));

const AdditionalCtaButton = styled(Button)(({ theme }) => ({
    padding: '6px 16px',
    height: 'initial',
    backgroundColor: theme.colors.secondaryBlueDarkBase,

    '&:hover': {
        backgroundColor: lighten(theme.colors.secondaryBlueDarkBase, 0.1),
    },
}));

const TextContainer = styled(Flex)(() => ({}));
